import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import TooltipWrapper from "../../../../components/common/TooltipWrapper";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import styles from "./styles.module.scss";
import BusinessTable, {
  RowWithOptionalClick,
} from "../../../../components/table";
import Pagination from "../../../../components/pagination";
import Spinner from "../../../../components/common/Spinner";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import CashoutBankFormModal from "./CashoutBankFormModal";
import ConfirmModal from "./ConfirmModal";
import {
  createCashoutBank,
  deleteCashoutBank,
  getActiveCashoutBank,
  getCashoutBanks,
  setCashoutBankEnabled,
  updateCashoutBank,
} from "../../../../setup/api/services/cashoutBanks";
import {
  ICashoutBank,
  ICreateCashoutBankBody,
  IUpdateCashoutBankBody,
} from "../../../../setup/api/services/cashoutBanks/models";

const PAGE_SIZE = 10;

/**
 * BusinessTable's default export is React.memo(...)-wrapped, which erases its generic type
 * parameter, so the row type has to nominally extend RowWithOptionalClick rather than rely on
 * structural compatibility (same workaround as ClientsInformation).
 */
interface CashoutBankRow extends RowWithOptionalClick, ICashoutBank {}

type ConfirmAction = "activate" | "deactivate" | "delete";

interface IConfirmState {
  action: ConfirmAction;
  bank: ICashoutBank;
}

/**
 * Admin management of the bank accounts OneSingleView cashouts are paid out from. Adding a bank
 * and making it the one money leaves from are deliberately two separate, individually confirmed
 * steps: a bank is always created disabled and activated afterwards from the table.
 */
const CashoutBanks = () => {
  const { t } = useTranslation();

  const columns = [
    { type: "name", value: t("lbl_cashout_bank_name") },
    { type: "bankCode", value: t("lbl_cashout_bank_bank_code") },
    { type: "swiftCode", value: t("lbl_cashout_bank_swift_code") },
    { type: "companyCode", value: t("lbl_cashout_bank_company_code") },
    { type: "accountNumber", value: t("lbl_cashout_bank_account_number") },
    { type: "iban", value: t("lbl_cashout_bank_iban") },
    { type: "enabled", value: t("lbl_cashout_bank_status") },
    { type: "actions", value: t("lbl_cashout_bank_actions") },
  ];

  const [banks, setBanks] = useState<CashoutBankRow[]>([]);
  const [activeBank, setActiveBank] = useState<ICashoutBank | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [saving, setSaving] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingBank, setEditingBank] = useState<ICashoutBank | null>(null);
  const [confirmState, setConfirmState] = useState<IConfirmState | null>(null);

  const fetchBanks = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getCashoutBanks(page - 1, PAGE_SIZE);
      setBanks((response?.data?.content ?? []) as CashoutBankRow[]);
      setPagesCount(response?.data?.totalPages ?? 1);
      setTotalCount(response?.data?.totalElements ?? 0);
    } catch (error) {
      console.error(error);
      toast.error(t("lbl_cashout_bank_load_failed"));
    } finally {
      setIsLoading(false);
    }
  }, [page, t]);

  /**
   * `GET /active` answers 404 when no bank is enabled — a normal state (the connector falls back
   * to its own configuration), so it clears the active bank instead of surfacing an error.
   */
  const fetchActiveBank = useCallback(async () => {
    try {
      const response = await getActiveCashoutBank();
      setActiveBank(response?.data ?? null);
    } catch (error) {
      setActiveBank(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([fetchBanks(), fetchActiveBank()]);
  }, [fetchBanks, fetchActiveBank]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  useEffect(() => {
    fetchActiveBank();
  }, [fetchActiveBank]);

  const bankLabel = (bank: ICashoutBank) =>
    bank.englishName || bank.arabicName || bank.bankCode;

  const handleOpenCreate = () => {
    setEditingBank(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (bank: ICashoutBank) => {
    setEditingBank(bank);
    setFormOpen(true);
  };

  const handleSubmitForm = async (
    body: ICreateCashoutBankBody | IUpdateCashoutBankBody
  ) => {
    setSaving(true);
    try {
      if (editingBank) {
        await updateCashoutBank(editingBank.id, body);
        toast.success(t("lbl_cashout_bank_updated"));
      } else {
        // Never `enabled: true` — activation is the separate second step.
        await createCashoutBank({
          ...(body as ICreateCashoutBankBody),
          enabled: false,
        });
        toast.success(t("lbl_cashout_bank_created"));
      }
      setFormOpen(false);
      setEditingBank(null);
      await refresh();
    } catch (error) {
      console.error(error);
      toast.error(
        editingBank
          ? t("lbl_cashout_bank_update_failed")
          : t("lbl_cashout_bank_create_failed")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmState) return;
    const { action, bank } = confirmState;
    setSaving(true);
    try {
      if (action === "delete") {
        await deleteCashoutBank(bank.id);
        toast.success(t("lbl_cashout_bank_deleted"));
      } else {
        await setCashoutBankEnabled(bank.id, action === "activate");
        toast.success(
          action === "activate"
            ? t("lbl_cashout_bank_activated")
            : t("lbl_cashout_bank_deactivated")
        );
      }
      setConfirmState(null);
      // Activating one bank silently disables another, so every row's flag is stale.
      await refresh();
    } catch (error) {
      console.error(error);
      // Defense in depth: the toggle is already disabled for a missing company code, but if the
      // backend still rejects the enable call (e.g. a stale row), surface its own message rather
      // than a generic one.
      const serverMessage = (error as { error?: { message?: string } })?.error
        ?.message;
      toast.error(
        serverMessage ||
          (action === "delete"
            ? t("lbl_cashout_bank_delete_failed")
            : t("lbl_cashout_bank_toggle_failed"))
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmCopy = () => {
    if (!confirmState) {
      return { title: "", message: "", confirmLabel: "", danger: false };
    }
    const { action, bank } = confirmState;

    if (action === "delete") {
      return {
        title: t("lbl_cashout_bank_delete_title"),
        message: bank.enabled
          ? t("lbl_cashout_bank_delete_active_message")
          : t("lbl_cashout_bank_delete_message"),
        confirmLabel: t("lbl_cashout_bank_delete"),
        danger: true,
      };
    }

    if (action === "deactivate") {
      return {
        title: t("lbl_cashout_bank_deactivate_title"),
        message: t("lbl_cashout_bank_deactivate_message"),
        confirmLabel: t("lbl_cashout_bank_deactivate"),
        danger: true,
      };
    }

    return {
      title: t("lbl_cashout_bank_activate_title"),
      message:
        activeBank && activeBank.id !== bank.id
          ? t("lbl_cashout_bank_activate_replace_message", {
              name: bankLabel(activeBank),
            })
          : t("lbl_cashout_bank_activate_message"),
      confirmLabel: t("lbl_cashout_bank_activate"),
      danger: false,
    };
  };

  const renderCell = (columnType: string, row: CashoutBankRow) => {
    switch (columnType) {
      case "name":
        return <span className={styles.nameValue}>{bankLabel(row)}</span>;
      case "bankCode":
        return (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {row.bankCode}
          </span>
        );
      case "swiftCode":
        return (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {row.swiftCode}
          </span>
        );
      case "companyCode":
        return row.companyCode ? (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {row.companyCode}
          </span>
        ) : (
          <TooltipWrapper
            id={`company-code-missing-${row.id}`}
            content={t("lbl_cashout_bank_company_code_missing_tooltip")}
            arabicDir>
            <span className={styles.missingValue}>
              {t("lbl_cashout_bank_company_code_missing")}
            </span>
          </TooltipWrapper>
        );
      case "accountNumber":
        return (
          <span className={styles.maskedValue} style={{ direction: "ltr" }}>
            {row.accountNumber}
          </span>
        );
      case "iban":
        return (
          <span className={styles.maskedValue} style={{ direction: "ltr" }}>
            {row.iban}
          </span>
        );
      case "enabled": {
        // A bank with no company code would fall back to the configured code, which belongs to a
        // different ordering account — OSV rejects that on a live payout, and the backend already
        // rejects the enable call. Disabling the switch surfaces that up front instead of letting
        // it fail server-side.
        const missingCompanyCode = !row.enabled && !row.companyCode;
        const toggle = (
          <Switch
            checked={row.enabled}
            disabled={saving || missingCompanyCode}
            onChange={(_, checked) =>
              setConfirmState({
                action: checked ? "activate" : "deactivate",
                bank: row,
              })
            }
          />
        );
        return missingCompanyCode ? (
          <TooltipWrapper
            id={`enable-disabled-${row.id}`}
            content={t("lbl_cashout_bank_company_code_missing_tooltip")}
            arabicDir>
            {toggle}
          </TooltipWrapper>
        ) : (
          toggle
        );
      }
      case "actions":
        return (
          <div className={styles.actionsCell}>
            <IconButton
              size="small"
              aria-label={t("lbl_cashout_bank_edit")}
              onClick={() => handleOpenEdit(row)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={t("lbl_cashout_bank_delete")}
              onClick={() => setConfirmState({ action: "delete", bank: row })}>
              <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
            </IconButton>
          </div>
        );
      default:
        return null;
    }
  };

  const copy = confirmCopy();

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div>
          <h3 className={styles.title}>{t("lbl_cashout_banks")}</h3>
          <p className={styles.subtitle}>{t("lbl_cashout_banks_subtitle")}</p>
        </div>
        <PrimaryButton onClick={handleOpenCreate} width="auto">
          {t("lbl_cashout_bank_add")}
        </PrimaryButton>
      </div>

      {activeBank ? (
        <div className={styles.activeBanner}>
          <span className={styles.activeLabel}>
            {t("lbl_cashout_bank_active_label")}
          </span>
          <span className={styles.activeValue}>
            {bankLabel(activeBank)}
            <span style={{ direction: "ltr" }}> — {activeBank.iban}</span>
            {activeBank.companyCode && (
              <span style={{ direction: "ltr" }}>
                {" "}
                · {t("lbl_cashout_bank_company_code")}: {activeBank.companyCode}
              </span>
            )}
          </span>
        </div>
      ) : (
        <div className={styles.fallbackBanner}>
          {t("lbl_cashout_bank_none_active")}
        </div>
      )}

      {isLoading ? (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <BusinessTable
              columns={columns}
              rows={banks}
              renderCell={(column, row) =>
                renderCell(column, row as CashoutBankRow)
              }
            />
          </div>
          {banks.length === 0 && (
            <p className={styles.emptyState}>{t("lbl_cashout_bank_empty")}</p>
          )}
          <Pagination
            countName={t("lbl_cashout_bank_row_name")}
            pagesCount={pagesCount}
            page={page}
            pageSize={PAGE_SIZE}
            length={totalCount}
            handlePaginationChange={(value) => setPage(value)}
          />
        </>
      )}

      <CashoutBankFormModal
        open={formOpen}
        bank={editingBank}
        saving={saving}
        handleClose={() => {
          setFormOpen(false);
          setEditingBank(null);
        }}
        handleConfirm={handleSubmitForm}
      />

      <ConfirmModal
        open={!!confirmState}
        title={copy.title}
        message={copy.message}
        confirmLabel={copy.confirmLabel}
        danger={copy.danger}
        saving={saving}
        handleClose={() => setConfirmState(null)}
        handleConfirm={handleConfirmAction}
      />
    </div>
  );
};

export default CashoutBanks;
